import allSettled from "../../utils/all-settled";
import extendAlmaGlobal from "../../utils/almaglobal";
import loadScript from "../../utils/loadscript";
import { resolveTargetingParamsSync } from "./ad-targeting/targeting-params";
import { XandrAdRequest, XandrAdEvent, } from "./adrequest";
import { AdsService, } from "./adsservice";
import { installCommandQueue, executeQueue } from "./commandqueue";
import { lazyloadEnabled } from "./helpers";
class XandrAdService extends AdsService {
    constructor(config, stack) {
        super("XandrAds", config, stack);
        this.commandQueue = [];
        this.requests = [];
        this.globalLoadAdQueue = [];
    }
    async bootstrapService() {
        if (!this.context.services?.dataLayerService) {
            throw new Error("Missing data layer");
        }
        await this.initAst(this.config.defaults.astJs.src ?? "https://acdn.adnxs.com/ast/ast.js");
        const apiExtension = {
            loadAd: (divId, params) => {
                try {
                    if (params?.reload === true) {
                        this.reloadAdPrivate(divId, params);
                        return;
                    }
                    if (Object.keys(params ?? {}).filter((p) => p !== "adRequestId")
                        .length > 0) {
                        this.logger.logWarn(`loadAd called with params that might be undefined behaviour`);
                    }
                    this.loadAdPrivate(divId, params?.adRequestId);
                }
                catch (e) {
                    this.logger.logError(`loadAd failed due to: ${e}`);
                }
            },
            init: async () => {
                try {
                    return (await this.init());
                }
                catch (e) {
                    this.logger.logError(`adInit failed due to: ${e}`);
                    return undefined;
                }
            },
            clear: (...args) => {
                try {
                    let targets;
                    if (args.length === 0) {
                        targets = undefined;
                    }
                    else if (Array.isArray(args[0]) || args.length > 0) {
                        let filteredRest = (args.length === 1 ? args[0] : args);
                        if (!Array.isArray(filteredRest)) {
                            filteredRest = [filteredRest];
                        }
                        targets = filteredRest.filter((slot) => typeof slot === "string");
                    }
                    this.clearAds(targets);
                }
                catch (e) {
                    this.logger.logError(`clearAds failed due to: ${e}`);
                }
            },
            requests: () => this.requests,
            getTargetingParams: (opts) => {
                try {
                    return this.getAdTargetingParameters(opts);
                }
                catch (e) {
                    this.logger.logError(`getTargetingParams failed due to: ${e}`);
                    return undefined;
                }
            },
            objToQs: (obj) => {
                try {
                    return XandrAdService.objToQs(obj);
                }
                catch (e) {
                    this.logger.logError(`objToQs failed due to: ${e}`);
                    return undefined;
                }
            },
            addPrefixSuffix: (obj, prefix, suffix) => {
                try {
                    return XandrAdService.addPrefixSuffix(obj, prefix, suffix);
                }
                catch (e) {
                    this.logger.logError(`addPrefixSuffix failed due to: ${e}`);
                    return undefined;
                }
            },
        };
        extendAlmaGlobal(apiExtension, "ad");
        if (this.config.defaults.autoInit === true) {
            await this.init();
        }
        executeQueue(this.commandQueue);
    }
    async init() {
        this.logger.logInfo(`Ad Init`);
        this.resetAstState();
        this.context.services.lazyloadService?.resetLazyloader();
        if (this.config.customNeeds?.preAdRequest) {
            this.logger.logInfo(`Found preAdRequest, calling it`);
            const result = this.config.customNeeds.preAdRequest(this.context);
            if (typeof result === "object" && typeof result.reason === "string") {
                this.logger.logInfo(`Cancelling current ad request, reason: ${result.reason}`);
                return undefined;
            }
            if (typeof result !== "boolean" ||
                (typeof result === "boolean" && result === false)) {
                if (result === false) {
                    this.logger.logWarn(`Hook returned false. You must explicitly cancel adRequest with { reason: string }`);
                }
                this.logger.logWarn(`Unknown result from preAdRequest, continue anyway`);
            }
            else if (result === true) {
                this.logger.logInfo(`Ok result from preAdRequest`);
            }
        }
        this.context.core?.recordEvent("xandrads.start", window.performance.now());
        const adRequest = new XandrAdRequest(this.context, this.config);
        this.requests.push(adRequest);
        // Do some validation so we can update info object or avoid doing pointless requests
        if (!adRequest.adSection) {
            this.logger.logError(`Ad request does not have a section`);
            // this.updateInfoObject(adRequest);
            return adRequest;
        }
        if (adRequest.placements.length === 0) {
            this.logger.logError(`Ad request does not have any placements configured`);
            // this.updateInfoObject(adRequest);
            return adRequest;
        }
        this.logger.logInfo(`Check Brandsafety, resolve kvs + device`);
        try {
            let ops = [];
            if (this.context.services.brandsafetyService) {
                this.logger.logInfo(`Brandsafety service available`);
                const resolveBs = async () => {
                    this.context.core?.recordEvent("adreq.bs.start", window.performance.now());
                    this.logger.logInfo(`Checking bs`);
                    const res = await this.context.services.brandsafetyService?.isSafe("ads");
                    this.context.core?.recordEvent("adreq.bs.end", window.performance.now());
                    this.logger.logInfo(`BS: ${res}`);
                    return res;
                };
                ops.push(resolveBs());
            }
            else {
                this.logger.logInfo(`Brandsafety service not available`);
            }
            ops = [
                ...ops,
                ...[
                    adRequest.resolveKeyValues(),
                    adRequest.resolveDeviceObject(),
                    adRequest.resolveUserObject(),
                ],
            ];
            const res = await allSettled(ops);
            // Brandsafety block check
            if (this.context.services.brandsafetyService &&
                res &&
                res[0].status === "fulfilled" &&
                typeof res[0].value === "boolean" &&
                res[0].value === false) {
                this.logger.logInfo("Ad request blocked by brand safety");
                return undefined;
            }
        }
        catch (e) {
            this.logger.logError(`Ad request failed due to: ${e}`);
        }
        try {
            this.logger.logInfo("Requesting consent from AlmaCmp");
            await this.context.services.almaCmpService?.getPublisherConsents();
            this.logger.logInfo("Received consent from AlmaCmp!");
        }
        catch (e) {
            this.logger.logError(`Error while getting consent ${e}`);
            return undefined;
        }
        if (this.globalLoadAdQueue.length > 0) {
            this.globalLoadAdQueue.forEach((req) => {
                adRequest.loadAd(req);
            });
            this.globalLoadAdQueue.length = 0;
        }
        await adRequest.resolvePrebid();
        await adRequest.requestAds();
        this.context.core?.recordEvent("xandrads.end", window.performance.now());
        // Update info object
        this.updateInfoObject(adRequest);
        return adRequest;
    }
    updateInfoObject(adRequest) {
        this.logger.logInfo(`Update info object`);
        extendAlmaGlobal({
            ...window?.ALMA.ad,
            info: {
                ...window.ALMA.ad?.info,
                section: adRequest.adSection,
                device: adRequest.device,
                placements: adRequest.placements,
                targeting: adRequest.keywords,
                user: adRequest.user,
            },
        }, "ad");
    }
    clearAds(slots) {
        const adEs = slots
            ? window.document.querySelectorAll(slots.join(","))
            : window.document.querySelectorAll(".ad");
        if (adEs.length > 0) {
            this.logger.logInfo(`Clearing ad slots ${Array.from(adEs).map((e) => e.id)}`);
            Array.from(adEs).forEach((e) => {
                e.innerHTML = "";
            });
        }
        else {
            this.logger.logInfo(`No ad slots to clear`);
        }
    }
    resetAstState() {
        this.logger.logInfo("Resetting ast.js state");
        Object.values(XandrAdEvent).forEach((eventType) => window.apntag.offEvent(eventType));
        window.apntag.clearRequest();
    }
    loadAdPrivate(divId, requestId) {
        let targetRequest = this.requests[this.requests.length - 1];
        if (requestId) {
            targetRequest = this.requests.find((rq) => rq.uuid === requestId);
        }
        if (targetRequest) {
            this.logger.logInfo(`loadAd requested for ${divId}, request ${targetRequest.uuid}`);
            targetRequest.loadAd(divId);
        }
        else {
            this.logger.logWarn(`loadAd called before init`);
            this.globalLoadAdQueue.push(divId);
        }
    }
    reloadAdPrivate(divId, params) {
        this.logger.logInfo(`Reload ad slot ${divId} with params ${JSON.stringify(params, null, 2)}`);
        if (window?.apntag?.requests?.tags &&
            !Object.keys(window.apntag.requests.tags).includes(divId)) {
            this.logger.logError(`Placement ${divId} not defined - cannot reload`);
            return;
        }
        window.apntag.anq.push(async () => {
            window.apntag.modifyTag(divId, {
                keywords: params.keywords,
            });
            const request = this.requests[this.requests.length - 1];
            const slot = request.placements.find((p) => p.divId === divId);
            if (slot && slot.prebid) {
                await this.context.services.prebidService?.prebidPlacements([slot]);
            }
            const lls = this.context.services.lazyloadService;
            if (lls && slot && lazyloadEnabled(slot)) {
                lls.lazyloadPlacement(divId, async () => {
                    this.logger.logInfo(`Lazyloading refresh for ${divId}`);
                    window.apntag.refresh([divId]);
                });
                this.context.services.lazyloadService?.activateObserver(divId);
            }
            else {
                this.logger.logInfo(`Refresh for ${divId}`);
                window.apntag.refresh([divId]);
            }
        });
    }
    setupPlacements(placements, externalImpId) {
        this.logger.logInfo(`Setting up ${placements.length} placements`);
        placements.forEach(({ lazyload, ...rest }) => {
            this.setupPlacement(rest, externalImpId);
            if (lazyload &&
                !("disable" in lazyload && lazyload.disable === false) &&
                typeof lazyload.offset !== "undefined") {
                this.logger.logInfo(`Set lazyload opts for ${rest.divId}`);
                this.context.services.lazyloadService?.setLazyloadOptions(rest.divId, lazyload);
            }
        });
    }
    setupPlacement(placement, externalImpId) {
        this.logger.logDebug(`Setting up placement: ${JSON.stringify(placement, null, 2)}`);
        let { allowedFormats } = this.config.defaults.astJs;
        if (placement.adType) {
            if (Array.isArray(placement.adType)) {
                allowedFormats = placement.adType;
            }
            else {
                allowedFormats = [placement.adType];
            }
        }
        let rendererOptions = allowedFormats.includes("video")
            ? this.config.outstream?.rendererOptions
            : undefined;
        if (this.config.outstream?.preRequest && rendererOptions) {
            this.logger.logInfo(`Calling outstream preRequest hook`);
            rendererOptions = this.config.outstream.preRequest(rendererOptions, this.context);
        }
        let params = {
            tagId: placement.placementId,
            sizes: placement.sizes,
            targetId: placement.divId,
            allowedFormats,
            rendererOptions,
        };
        if (params.allowedFormats?.includes("native") &&
            this.context.services.nativeAdsService) {
            if (placement.native?.nativeFields) {
                params = {
                    ...params,
                    native: placement.native?.nativeFields,
                };
            }
            else {
                params = {
                    ...params,
                    native: this.context.services.nativeAdsService.getNativeParams(),
                };
            }
        }
        if (externalImpId) {
            params = { ...params, externalImpId };
        }
        window.apntag.defineTag(params);
    }
    static modifyTagRequest(divId, params) {
        window.apntag.modifyTag(divId, params);
    }
    setPageOpts(extraPageOpts) {
        const defaultPageOpts = {
            member: this.config.defaults.memberId,
            disablePsa: this.config.defaults.astJs.disablePsa,
            consentManagement: {
                usp: {
                    disabled: true,
                },
            },
        };
        const pageOpts = { ...defaultPageOpts, ...extraPageOpts };
        this.logger.logInfo(`Settings pageopts: ${JSON.stringify(pageOpts, null, 2)}`);
        window.apntag.setPageOpts(pageOpts);
    }
    static makeAdRequest() {
        window.apntag.loadTags();
    }
    async initAst(src) {
        this.logger.logInfo(`Checking for apntag.loaded`);
        if (!window?.apntag?.loaded) {
            this.logger.logInfo(`apntag.loaded not found, initialize ast.js from ${src}`);
            window.apntag = window.apntag || {};
            window.apntag.anq = window.apntag.anq || [];
            await loadScript(src, window);
        }
        this.logger.logInfo("ast.js is loaded");
        this.commandQueue = installCommandQueue();
    }
    getAdTargetingParameters(opts) {
        this.logger.logInfo(`Ad targeting params requested externally: ${opts && Object.entries(opts).map(([k, v]) => `${k}:${v}`)}`);
        let params = resolveTargetingParamsSync(this.config.targeting, this.context);
        if (opts?.remove && opts.remove.length > 0) {
            params = XandrAdService.removeKeys(params, opts.remove);
        }
        if (opts?.prefix || opts?.suffix) {
            params = XandrAdService.addPrefixSuffix(params, opts.prefix, opts.suffix);
        }
        if (opts?.type === "qs") {
            params = XandrAdService.objToQs(params);
        }
        return params;
    }
    static objToQs(obj) {
        const str = Object.entries(obj).reduce((acc, prop) => {
            return [
                ...acc,
                `${encodeURIComponent(prop[0])}=${encodeURIComponent(prop[1])}`,
            ];
        }, []);
        return str.join("&");
    }
    static addPrefixSuffix(obj, prefix, suffix) {
        const tmp = {};
        const keys = Object.keys(obj);
        for (let x = 0; x < keys.length; x += 1) {
            const key = keys[x].replace(keys[x], (prefix || "") + keys[x] + (suffix || ""));
            tmp[key] = obj[keys[x]];
        }
        return tmp;
    }
    static removeKeys(obj, keys) {
        const tmp = { ...obj };
        keys.forEach((k) => delete tmp[k]);
        return tmp;
    }
}
export default XandrAdService;
//# sourceMappingURL=xandrads.js.map